import QRCode from 'qrcode';
import { formatPrice, formatDateTime } from './format';
import type { Sale, SaleItem } from '@/types';

const escapeHtml = (s: string): string =>
  s.replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c] as string));

const qtyLabel = (it: SaleItem): string => {
  const q = Number(it.quantity) || 0;
  const nice = q % 1 === 0 ? String(q) : String(q);
  if (it.quantity_unit === 'gram') return `${nice} g`;
  if (it.quantity_unit === 'kilogram') return `${nice} kg`;
  return nice;
};

const paymentLabel = (sale: Sale): string => {
  if (sale.is_split) return 'Pago dividido';
  const map: Record<string, string> = {
    cash: 'Efectivo',
    card: 'Tarjeta',
    transfer: 'Transferencia',
    qr: 'QR',
  };
  return map[sale.payment_method as string] || String(sale.payment_method || '');
};

const buildItemRows = (sale: Sale): string =>
  sale.items
    .map((it) => {
      const name = it.product_name || it.custom_name || 'Producto';
      const lineTotal = formatPrice(it.subtotal);
      const hasDisc = Number(it.discount_amount) > 0 || Number(it.discount_percentage) > 0;
      const discTxt = hasDisc
        ? `<div class="disc">Desc. ${
            Number(it.discount_percentage) > 0 ? Number(it.discount_percentage) + '%' : formatPrice(it.discount_amount)
          }</div>`
        : '';
      return `
        <div class="item">
          <div class="line"><span class="name">${escapeHtml(qtyLabel(it))}x ${escapeHtml(name)}</span></div>
          <div class="line amt"><span>${lineTotal}</span></div>
          ${discTxt}
        </div>`;
    })
    .join('');

/**
 * Imprime un HTML de ticket (80mm) vía iframe oculto (sin ventana emergente).
 * Con el navegador en "kiosk printing" y una sola impresora, sale directo sin diálogo.
 */
function printHtmlInIframe(html: string): void {
  const iframe = document.createElement('iframe');
  iframe.setAttribute('aria-hidden', 'true');
  iframe.style.position = 'fixed';
  iframe.style.right = '0';
  iframe.style.bottom = '0';
  iframe.style.width = '0';
  iframe.style.height = '0';
  iframe.style.border = '0';
  document.body.appendChild(iframe);

  const doc = iframe.contentWindow?.document;
  if (!doc || !iframe.contentWindow) {
    iframe.remove();
    return;
  }
  doc.open();
  doc.write(html);
  doc.close();

  const win = iframe.contentWindow;
  let cleaned = false;
  const cleanup = () => {
    if (cleaned) return;
    cleaned = true;
    setTimeout(() => iframe.remove(), 1500);
  };
  win.onafterprint = cleanup;

  setTimeout(() => {
    try {
      win.focus();
      win.print();
    } catch {
      /* ignore */
    }
  }, 350);
  setTimeout(cleanup, 60000);
}

/**
 * Abre el ticket de una venta en una ventana lista para imprimir en ticketera (80mm).
 * storeName sale del nombre del local (tenant); si no hay, usa "Nutritienda Ines".
 */
export function printSaleTicket(
  sale: Sale,
  store?: { name?: string; cuit?: string; address?: string },
): void {
  const title = (store?.name && store.name.trim()) || 'Nutritienda Ines';
  const cuit = store?.cuit?.trim() || '';
  const address = store?.address?.trim() || '';

  const rows = buildItemRows(sale);

  // Descuento global de la venta (subtotal - total). Cubre tanto % como monto fijo.
  const overallDiscount = Number(sale.subtotal) - Number(sale.total);
  const showSubtotal = overallDiscount > 0.009;

  const customerLine = sale.customer_name
    ? `<div class="meta">Cliente: ${escapeHtml(sale.customer_name)}</div>`
    : '';

  const html = `<!doctype html>
<html>
<head>
<meta charset="utf-8" />
<title>Ticket ${escapeHtml(sale.receipt_number || '')}</title>
<style>
  /* size: auto en alto -> el ticket sale del largo del contenido (no fijo de 800mm) */
  @page { size: 72mm auto; margin: 0; }
  * { box-sizing: border-box; }
  html, body { margin: 0; padding: 0; height: auto; }
  /* font-weight fuerte + negro puro para que la térmica marque más oscuro */
  body {
    font-family: 'Courier New', monospace;
    font-size: 12px;
    color: #000;
    width: 72mm;
    padding: 2mm 3mm;
    font-weight: 700;
    -webkit-print-color-adjust: exact;
    print-color-adjust: exact;
  }
  .center { text-align: center; }
  .title { font-size: 16px; font-weight: bold; letter-spacing: .5px; margin-bottom: 2px; }
  .meta { font-size: 11px; }
  .sep { border-top: 1px dashed #000; margin: 6px 0; }
  .item { margin: 2px 0; }
  .line { display: flex; justify-content: space-between; }
  .line.amt { justify-content: flex-end; font-weight: bold; }
  .name { word-break: break-word; }
  .disc { font-size: 10px; text-align: right; }
  .totals .row { display: flex; justify-content: space-between; margin: 1px 0; }
  .totals .grand { font-size: 15px; font-weight: bold; border-top: 1px solid #000; padding-top: 3px; margin-top: 3px; }
  .foot { text-align: center; margin-top: 8px; font-size: 11px; }
</style>
</head>
<body>
  <div class="center">
    <div class="title">${escapeHtml(title)}</div>
    ${cuit ? `<div class="meta">CUIT: ${escapeHtml(cuit)}</div>` : ''}
    ${address ? `<div class="meta">${escapeHtml(address)}</div>` : ''}
    <div class="sep"></div>
    <div class="meta">${escapeHtml(formatDateTime(sale.created_at))}</div>
    ${sale.receipt_number ? `<div class="meta">Ticket ${escapeHtml(sale.receipt_number)}</div>` : ''}
  </div>
  ${customerLine}
  <div class="sep"></div>
  ${rows}
  <div class="sep"></div>
  <div class="totals">
    ${showSubtotal ? `<div class="row"><span>Subtotal</span><span>${formatPrice(sale.subtotal)}</span></div>` : ''}
    ${showSubtotal ? `<div class="row"><span>Descuento</span><span>-${formatPrice(overallDiscount)}</span></div>` : ''}
    <div class="row grand"><span>TOTAL</span><span>${formatPrice(sale.total)}</span></div>
  </div>
  <div class="sep"></div>
  <div class="meta center">Forma de pago: ${escapeHtml(paymentLabel(sale))}</div>
  <div class="foot">¡Gracias por su compra!</div>
  <div class="sep"></div>
  <div class="center" style="font-size:10px;">Este documento no tiene valor fiscal</div>
  <div class="sep"></div>
</body>
</html>`;

  printHtmlInIframe(html);
}

// dd/mm/yyyy a partir de "yyyy-mm-dd"
const fmtDateAR = (iso?: string | null): string => {
  if (!iso) return '';
  const [y, m, d] = iso.slice(0, 10).split('-');
  return d && m && y ? `${d}/${m}/${y}` : iso;
};

// Etiqueta del receptor según el tipo de documento de ARCA
const receptorLabel = (sale: Sale): string => {
  if (sale.e_doc_tipo === 80 && sale.e_doc_nro) return `CUIT: ${sale.e_doc_nro}`;
  if (sale.e_doc_tipo === 96 && sale.e_doc_nro) return `DNI: ${sale.e_doc_nro}`;
  return 'Consumidor Final';
};

/**
 * Imprime la FACTURA C electrónica (con valor fiscal) en la térmica: incluye
 * el N° de comprobante, CAE, vencimiento y el QR de ARCA. La venta ya tiene que
 * tener el CAE emitido (sale.cae + sale.e_qr_url).
 */
export async function printElectronicTicket(
  sale: Sale,
  store?: { name?: string; cuit?: string; address?: string },
): Promise<void> {
  const title = (store?.name && store.name.trim()) || 'Nutritienda Ines';
  const cuit = store?.cuit?.trim() || '';
  const address = store?.address?.trim() || '';

  const rows = buildItemRows(sale);
  const overallDiscount = Number(sale.subtotal) - Number(sale.total);
  const showSubtotal = overallDiscount > 0.009;

  const ptoVta = String(sale.e_pto_vta ?? '').padStart(4, '0');
  const nro = String(sale.e_cbte_nro ?? '').padStart(8, '0');

  // QR de ARCA como imagen (data URL). Si falla, seguimos sin QR.
  let qrImg = '';
  if (sale.e_qr_url) {
    try {
      const dataUrl = await QRCode.toDataURL(sale.e_qr_url, { margin: 1, width: 240 });
      qrImg = `<img src="${dataUrl}" alt="QR AFIP" style="width:36mm;height:36mm;" />`;
    } catch {
      /* sin QR */
    }
  }

  const html = `<!doctype html>
<html>
<head>
<meta charset="utf-8" />
<title>Factura C ${escapeHtml(`${ptoVta}-${nro}`)}</title>
<style>
  @page { size: 72mm auto; margin: 0; }
  * { box-sizing: border-box; }
  html, body { margin: 0; padding: 0; height: auto; }
  body {
    font-family: 'Courier New', monospace;
    font-size: 12px;
    color: #000;
    width: 72mm;
    padding: 2mm 3mm;
    font-weight: 700;
    -webkit-print-color-adjust: exact;
    print-color-adjust: exact;
  }
  .center { text-align: center; }
  .title { font-size: 16px; font-weight: bold; letter-spacing: .5px; margin-bottom: 2px; }
  .cbte { font-size: 15px; font-weight: bold; margin: 4px 0; }
  .meta { font-size: 11px; }
  .sep { border-top: 1px dashed #000; margin: 6px 0; }
  .item { margin: 2px 0; }
  .line { display: flex; justify-content: space-between; }
  .line.amt { justify-content: flex-end; font-weight: bold; }
  .name { word-break: break-word; }
  .disc { font-size: 10px; text-align: right; }
  .totals .row { display: flex; justify-content: space-between; margin: 1px 0; }
  .totals .grand { font-size: 15px; font-weight: bold; border-top: 1px solid #000; padding-top: 3px; margin-top: 3px; }
  .cae { font-size: 11px; text-align: center; margin-top: 4px; }
  .foot { text-align: center; margin-top: 6px; font-size: 11px; }
</style>
</head>
<body>
  <div class="center">
    <div class="title">${escapeHtml(title)}</div>
    ${cuit ? `<div class="meta">CUIT: ${escapeHtml(cuit)}</div>` : ''}
    ${address ? `<div class="meta">${escapeHtml(address)}</div>` : ''}
    <div class="meta">Responsable Monotributo</div>
    <div class="sep"></div>
    <div class="cbte">FACTURA C</div>
    <div class="meta">Comprobante N° ${escapeHtml(`${ptoVta}-${nro}`)}</div>
    <div class="meta">Fecha: ${escapeHtml(fmtDateAR(sale.e_fecha) || formatDateTime(sale.created_at))}</div>
  </div>
  <div class="sep"></div>
  <div class="meta">${escapeHtml(receptorLabel(sale))}</div>
  ${sale.customer_name ? `<div class="meta">Cliente: ${escapeHtml(sale.customer_name)}</div>` : ''}
  <div class="sep"></div>
  ${rows}
  <div class="sep"></div>
  <div class="totals">
    ${showSubtotal ? `<div class="row"><span>Subtotal</span><span>${formatPrice(sale.subtotal)}</span></div>` : ''}
    ${showSubtotal ? `<div class="row"><span>Descuento</span><span>-${formatPrice(overallDiscount)}</span></div>` : ''}
    <div class="row grand"><span>TOTAL</span><span>${formatPrice(sale.total)}</span></div>
  </div>
  <div class="sep"></div>
  <div class="meta center">Forma de pago: ${escapeHtml(paymentLabel(sale))}</div>
  <div class="sep"></div>
  <div class="center">${qrImg}</div>
  <div class="cae">CAE: ${escapeHtml(sale.cae || '')}<br/>Vto CAE: ${escapeHtml(fmtDateAR(sale.cae_vencimiento))}</div>
  <div class="foot">¡Gracias por su compra!</div>
  <div class="sep"></div>
</body>
</html>`;

  printHtmlInIframe(html);
}
